import React, { useState, useEffect, useCallback } from 'react';
import { useFirebase } from '../../../context/FirebaseContext';
import { getWebhooks, createWebhook, updateWebhook, regenerateWebhookSecret, deleteWebhook } from '../../../api/journal';
import { getApiOrigin } from '../../../api/core';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckIcon from '@mui/icons-material/Check';
import WebhookIcon from '@mui/icons-material/Webhook';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import './TradeHistoryWebhookModal.css';

/**
 * TradeHistoryWebhookModal - manage webhooks for saving trades
 * Each webhook is for a specific account + instrument combination
 * Uses the currently selected instrument from the UI
 */
const TradeHistoryWebhookModal = ({ open, onClose, accounts, selectedInstrument, onAccountUpdate }) => {
    const { auth } = useFirebase();

    const [webhooks, setWebhooks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [copied, setCopied] = useState(null);
    const [showHelp, setShowHelp] = useState(false);
    const [showCreateForm, setShowCreateForm] = useState(false);

    // Create form state - instrument comes from selectedInstrument prop
    const [newAccountId, setNewAccountId] = useState('');

    // Load webhooks
    const loadWebhooks = useCallback(async () => {
        if (!auth?.currentUser) return;
        setLoading(true);
        try {
            await getWebhooks(auth, (result) => {
                if (result && !result.error) {
                    setWebhooks(result);
                } else {
                    setError(result?.error || 'Failed to load webhooks');
                }
            });
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, [auth]);

    useEffect(() => {
        if (open) {
            loadWebhooks();
            setError(null);
        }
    }, [open, loadWebhooks]);

    // Set default account when accounts load
    useEffect(() => {
        if (accounts.length > 0 && !newAccountId) {
            setNewAccountId(accounts[0].account_id.toString());
        }
    }, [accounts, newAccountId]);

    if (!open) return null;

    const getFullWebhookUrl = (webhookUrl) => {
        if (!webhookUrl) return '';
        return webhookUrl.startsWith('/') ? `${getApiOrigin()}${webhookUrl}` : webhookUrl;
    };

    const handleCopyUrl = async (webhookId, webhookUrl) => {
        try {
            await navigator.clipboard.writeText(getFullWebhookUrl(webhookUrl));
            setCopied(webhookId);
            setTimeout(() => setCopied(null), 2000);
        } catch (e) {
            setError('Failed to copy to clipboard');
        }
    };

    const handleCreateWebhook = async () => {
        if (!newAccountId || !selectedInstrument) {
            setError('Account and instrument are required');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            // Normalize instrument: remove underscores and uppercase
            const normalizedInstrument = selectedInstrument.replace('_', '').toUpperCase();
            await createWebhook(auth, (result) => {
                if (result && !result.error) {
                    setWebhooks(prev => [...prev, result]);
                    setShowCreateForm(false);
                } else {
                    setError(result?.error || 'Failed to create webhook');
                }
            }, parseInt(newAccountId), normalizedInstrument);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    const handleRegenerateSecret = async (webhookId) => {
        if (!window.confirm('Regenerate secret? This will invalidate the current URL.')) return;
        setLoading(true);
        try {
            await regenerateWebhookSecret(auth, (result) => {
                if (result && !result.error) {
                    setWebhooks(prev => prev.map(wh =>
                        wh.webhook_id === webhookId ? { ...wh, webhookUrl: result.webhookUrl } : wh
                    ));
                } else {
                    setError(result?.error || 'Failed to regenerate secret');
                }
            }, webhookId);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteWebhook = async (webhookId) => {
        if (!window.confirm('Delete this webhook? This cannot be undone.')) return;
        setLoading(true);
        try {
            await deleteWebhook(auth, (result) => {
                if (result && !result.error) {
                    setWebhooks(prev => prev.filter(wh => wh.webhook_id !== webhookId));
                } else {
                    setError(result?.error || 'Failed to delete webhook');
                }
            }, webhookId);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    const handleToggleEnabled = async (webhook) => {
        setLoading(true);
        try {
            await updateWebhook(auth, (result) => {
                if (result && !result.error) {
                    setWebhooks(prev => prev.map(wh =>
                        wh.webhook_id === webhook.webhook_id
                            ? { ...wh, webhook_enabled: !webhook.webhook_enabled }
                            : wh
                    ));
                }
            }, webhook.webhook_id, !webhook.webhook_enabled);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <div className="trade-history-webhook-overlay" onClick={handleOverlayClick}>
            <div className="trade-history-webhook-modal">
                <div className="webhook-modal-header">
                    <div className="header-title">
                        <WebhookIcon className="header-icon" />
                        <h3>Trade Webhooks</h3>
                    </div>
                    <button className="close-btn" onClick={onClose}>
                        <CloseIcon fontSize="small" />
                    </button>
                </div>

                <div className="webhook-modal-content">
                    <p className="modal-description">
                        Create webhooks to save trades from external systems. Each webhook is configured for a specific account, instrument, and source.
                    </p>

                    {error && <div className="webhook-error">{error}</div>}

                    {/* Webhooks List */}
                    <div className="webhooks-list">
                        {webhooks.length === 0 && !loading && (
                            <div className="no-webhooks">No webhooks configured yet</div>
                        )}

                        {webhooks.map(webhook => (
                            <div key={webhook.webhook_id} className={`webhook-item ${webhook.webhook_enabled ? '' : 'disabled'}`}>
                                <div className="webhook-info">
                                    <div className="webhook-primary">
                                        <span className="webhook-instrument">{webhook.instrument}</span>
                                        <span className="webhook-account">{webhook.account_name}</span>
                                    </div>
                                </div>

                                <div className="webhook-url-row">
                                    <input
                                        type="text"
                                        value={getFullWebhookUrl(webhook.webhookUrl)}
                                        readOnly
                                        className="url-input"
                                    />
                                    <button
                                        className={`url-btn ${copied === webhook.webhook_id ? 'copied' : ''}`}
                                        onClick={() => handleCopyUrl(webhook.webhook_id, webhook.webhookUrl)}
                                        title="Copy URL"
                                    >
                                        {copied === webhook.webhook_id ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                    </button>
                                    <button
                                        className="url-btn"
                                        onClick={() => handleRegenerateSecret(webhook.webhook_id)}
                                        disabled={loading}
                                        title="Regenerate secret"
                                    >
                                        <RefreshIcon fontSize="small" />
                                    </button>
                                    <button
                                        className="url-btn delete"
                                        onClick={() => handleDeleteWebhook(webhook.webhook_id)}
                                        disabled={loading}
                                        title="Delete webhook"
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </button>
                                </div>

                                <div className="webhook-toggle-row">
                                    <span className="toggle-label">
                                        {webhook.webhook_enabled ? 'Enabled' : 'Disabled'}
                                    </span>
                                    <button
                                        className={`toggle-btn ${webhook.webhook_enabled ? 'enabled' : ''}`}
                                        onClick={() => handleToggleEnabled(webhook)}
                                        disabled={loading}
                                    >
                                        <span className="toggle-slider" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Create New Webhook */}
                    {!showCreateForm ? (
                        <button className="add-webhook-btn" onClick={() => setShowCreateForm(true)}>
                            <AddIcon fontSize="small" />
                            <span>Add Webhook</span>
                        </button>
                    ) : (
                        <div className="create-webhook-form">
                            <h4>Create New Webhook</h4>

                            <div className="form-row">
                                <label>Account</label>
                                <select
                                    value={newAccountId}
                                    onChange={(e) => setNewAccountId(e.target.value)}
                                >
                                    {accounts.map(acc => (
                                        <option key={acc.account_id} value={acc.account_id}>
                                            {acc.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-row">
                                <label>Instrument</label>
                                <div className="instrument-display">
                                    {selectedInstrument ? selectedInstrument.replace('_', '/') : 'No instrument selected'}
                                </div>
                                <span className="form-hint">Using currently selected instrument. Callers identify themselves via sourceId.</span>
                            </div>

                            <div className="form-actions">
                                <button className="cancel-btn" onClick={() => setShowCreateForm(false)}>
                                    Cancel
                                </button>
                                <button
                                    className="create-btn"
                                    onClick={handleCreateWebhook}
                                    disabled={loading || !newAccountId || !selectedInstrument}
                                >
                                    Create Webhook
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Help Section */}
                    <button className="help-toggle" onClick={() => setShowHelp(!showHelp)}>
                        <HelpOutlineIcon fontSize="small" />
                        <span>{showHelp ? 'Hide' : 'Show'} API documentation</span>
                    </button>

                    {showHelp && (
                        <div className="help-card">
                            <h4>How to use Trade Webhooks</h4>
                            <p>Send a POST request to save a trade:</p>
                            <pre className="help-code">
{`POST /api/v2/webhook/trades/{webhookId}/{secret}
POST /api/v2/webhook/trades/{webhookId}/{secret}?sourceId=my-bot
Content-Type: application/json

{
  "direction": "LONG",
  "entryPrice": 45000.00,
  "exitPrice": 46500.00,
  "stopLoss": 44000.00,
  "takeProfit": 47000.00,
  "profitLoss": 150.00,
  "size": 0.1,
  "sourceId": "my-bot-v2",
  "notes": "Breakout trade"
}`}
                            </pre>
                            <p><strong>Note:</strong> Instrument and account are configured in the webhook. sourceId can be passed via query param, body, or use webhook default.</p>
                            <p>
                                <strong>Required fields:</strong><br/>
                                <code>direction</code> - LONG or SHORT<br/>
                                <code>entryPrice</code> - Entry price<br/>
                                <code>profitLoss</code> - Realized P/L
                            </p>
                            <p>
                                <strong>Optional fields:</strong><br/>
                                <code>exitPrice</code>, <code>stopLoss</code>, <code>takeProfit</code>, <code>size</code>, <code>sourceId</code>, <code>notes</code>
                            </p>
                            <p>
                                <strong>sourceId priority:</strong> query param {'>'} body {'>'} webhook config
                            </p>
                            <p>
                                <strong>Rate Limits:</strong><br/>
                                Free: 10/day | Basic: 100/day | Pro: 1,000/day | Unlimited: No limit
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TradeHistoryWebhookModal;
